import { useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CampoDoMotivo } from '@/componentes/CampoDoMotivo';
import { Seletor } from '@/componentes/Seletor';
import { EXEMPLO_DO_MOTIVO, faltaNoMotivo } from '@/lib/motivo';

import { MensagemDeErro } from './Avisos';
import { useGravarInicioDoPapel } from './consultas';
import type { Papel } from './tipos';

type Inicio = 'padrao' | 'abastecimento';

/** O que cada início mostra, na frase que a tela escreve. Puro. */
const INICIO_ESCRITO: Readonly<Record<Inicio, { readonly nome: string; readonly texto: string }>> = {
  padrao: {
    nome: 'Painel',
    texto: 'O início de sempre: os indicadores da empresa e as exceções do dia.',
  },
  abastecimento: {
    nome: 'Abastecimento',
    texto:
      'Os números do dia de quem abastece (litros e lançamentos, hoje e na semana) e o histórico do que essa pessoa lançou, com o que ainda está no aparelho. Funciona sem sinal.',
  },
};

/**
 * 0130 · O INÍCIO DO APP DO PAPEL — a aba do papel, em Papéis (decisão 55).
 *
 * O dono (28/10/2026): o perfil de quem abastece abre o app no histórico de
 * abastecimentos e nos números do dia dele, "e faremos esse tipo de
 * personalização em outros perfis". O papel nasce no Painel; quem administra
 * escolhe outro início, escreve o motivo e grava. Vale para todas as pessoas
 * com o papel, quando elas abrirem o app de novo.
 *
 * A pessoa com dois papéis que escolhem inícios diferentes abre no Painel: a
 * aba diz isso, para ninguém achar que escolheu e não pegou.
 */
export function InicioDoPapel({ papel, gere }: { readonly papel: Papel; readonly gere: boolean }) {
  const gravar = useGravarInicioDoPapel(papel);
  const atual: Inicio = papel.inicio_do_app ?? 'padrao';
  const [escolhido, setEscolhido] = useState<Inicio>(atual);
  const [motivo, setMotivo] = useState('');
  const [tentou, setTentou] = useState(false);
  const lanca = papel.lanca_abastecimento === true;
  const faltaMotivo = faltaNoMotivo(motivo);
  const semMudanca = escolhido === atual;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h3 className="txt-subtitulo text-tinta">Início do app</h3>
        <p className="txt-corpo text-tinta">
          <span className="txt-rotulo-campo">Hoje: </span>
          {INICIO_ESCRITO[atual].nome}
        </p>
        <p className="txt-apoio text-pretty text-tinta-secundaria">
          É a tela que o celular abre para quem tem este papel. Quem tiver outro papel com outro início abre no Painel, que serve às
          duas funções.
        </p>
      </div>

      {gere ? (
        <form
          className="flex flex-col gap-3 border-t border-borda-sutil pt-4"
          noValidate
          onSubmit={(evento) => {
            evento.preventDefault();
            setTentou(true);
            if (faltaMotivo || semMudanca) return;
            gravar.mutate(
              { inicio: escolhido, versao: papel.versao, motivo: motivo.trim() },
              {
                onSuccess: () => {
                  setMotivo('');
                  setTentou(false);
                },
              },
            );
          }}
        >
          <MensagemDeErro erro={gravar.error} />
          <div className="flex flex-col gap-2">
            <span className="txt-rotulo-campo">Início</span>
            <Seletor<Inicio>
              rotulo="Início do app"
              valor={escolhido}
              opcoes={[
                { valor: 'padrao', rotulo: INICIO_ESCRITO.padrao.nome },
                ...(lanca || atual === 'abastecimento' ? [{ valor: 'abastecimento' as const, rotulo: INICIO_ESCRITO.abastecimento.nome }] : []),
              ]}
              aoEscolher={setEscolhido}
            />
            <p className="txt-apoio text-pretty text-tinta-secundaria">{INICIO_ESCRITO[escolhido].texto}</p>
            {lanca ? null : (
              <p className="txt-apoio text-pretty text-tinta-secundaria">
                O início de Abastecimento aparece no papel que lança abastecimento (com a permissão abastecimento.registrar).
              </p>
            )}
          </div>

          <CampoDoMotivo valor={motivo} aoMudar={setMotivo} exemplo={`Por exemplo: ${EXEMPLO_DO_MOTIVO['papel.definir_inicio']}`} />
          {tentou && faltaMotivo ? (
            <p role="alert" className="txt-apoio text-erro-texto">
              {faltaMotivo}
            </p>
          ) : null}
          {tentou && semMudanca && !faltaMotivo ? (
            <p role="alert" className="txt-apoio text-erro-texto">
              O início escolhido é o de hoje. Escolha outro para gravar.
            </p>
          ) : null}

          <div className="flex justify-end">
            <Botao nivel="secundario" type="submit" disabled={gravar.isPending}>
              {gravar.isPending ? 'Gravando o início…' : 'Salvar início'}
            </Botao>
          </div>
        </form>
      ) : (
        <p className="txt-apoio text-tinta-secundaria">Quem escolhe o início do papel é o perfil Administrador.</p>
      )}
    </div>
  );
}
